import { Component, Vue } from 'vue-property-decorator';
import LocalStoreService from '@/store/local.store';
import BlobConverter from '@/utils/blobconverter';
import DynamicConfirmation from '@/components/DynamicConfirmation/DynamicConfirmation.vue';
import { GetterTypes } from '@/store-vuex/types';
import moment from 'moment';
import { useEquestHome } from '@/config/app.config';
import { McsisConstants } from '@/models/constants/McsisConstants';
import { ParticipantDTO } from '@/models/ParticipantDTO';
import EavService from '@/services/eav.services';
import { AppointmentDTO } from '@/models/AppointmentDTO';
import { EavAttributeName, EavClassName, EavParent } from '@/models/constants/eav';

@Component({
  components: {
    DynamicConfirmation,
  },
})
export default class Confirmation extends Vue {
  
  private isEQuestUsedByCenter = false;
  
  protected async mounted(): Promise<void> {
    if (this.appointment.center != null && this.appointment.center.personId != null)
    {
      await EavService.getBooleanEav(
        EavParent.EXAM_CENTER, 
        this.appointment.center.personId, 
        EavClassName.EXAM_CENTER_PARAM, 
        EavAttributeName.USE_EQUESTIONNAIRE
      ).then((res: boolean) => {
        this.isEQuestUsedByCenter = res;
      });
    }
  }

  private get participant(): ParticipantDTO {
    return this.$store.getters[GetterTypes.participant];
  }

  private get appointment(): AppointmentDTO {
    return this.$store.getters[GetterTypes.appointment];
  }

  protected get showEquestLink(): boolean {
    return useEquestHome(this.$route.params.screening)
      && !this.isModified
      && (this.isEQuestUsedByCenter);
  }

  private get equestRouteName(): string {
    const equest = process.env.VUE_APP_EQUEST_ROOT;
    const suName = this.$route.params.screening;
    const birthDate = moment(this.participant.birthDate!).format(McsisConstants.SwissDateFormat);
    const partId = this.participant.partId;

    return `${equest}/${suName}/at-home/identification?partId=${partId}&birthDate=${birthDate}`;
  }

  private get suName() { return this.$route.params.screening; }

  private get isModified(): boolean {
    return this.$store.getters[GetterTypes.isRdvModified];
  }

  private get isDeleted(): boolean {
    // Variable in the store that will be set in the onDelete method (Recap.ts) and used here to get the right confirmation text
    return this.$store.getters[GetterTypes.isDeleted];
  }

  /**
   * Get the formatted appointment date for display
   */
  private get formattedAppointmentDate(): string {
    if (this.appointment.appDate) {
      return moment(this.appointment.appDate).format(McsisConstants.SwissDateFormat);
    }
    return '';
  }

  /**
   * Get the formatted appointment time for display
   */
  private get formattedAppointmentTime(): string {
    if (this.appointment.appDate) {
      return moment(this.appointment.appDate).format('HH:mm');
    }
    return '';
  }

  /**
   * Get the center name for display
   */
  private get centerName(): string {
    if (this.appointment.center && this.appointment.center.companyName) {
      return this.appointment.center.companyName;
    }
    return '';
  }

  private get confirmationMessage(): string {
    const dateParam = { date: this.formattedAppointmentDate, time: this.formattedAppointmentTime, center: this.centerName };

    if (this.isModified) {
      return this.$t('page.confirmation.modificationMessage', dateParam) as string;
    } else if (this.isDeleted) {
      return this.$t('page.confirmation.deleteMessage') as string;
    } else {
      // creation or validation of an auto-allocation
      return this.$t('page.confirmation.validationMessage', dateParam) as string;
    }
  }

  /**
   * @brief Download the follow-up only if we already have a filename and it's an inclusion.
   */
  protected downloadFile(): void {
    const filename = LocalStoreService.getItem(LocalStoreService.localItemKeys.followUpFilename);
    if (filename) {
      const fileString = LocalStoreService.getItem(filename);
      if (fileString) {
        const blobPdf = BlobConverter.b64toBlob(fileString, 'application/pdf');
        const url = window.URL.createObjectURL(blobPdf);
        const link = document.createElement('a');

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  /**
   * @brief Remove the PDF related data
   */
  protected beforeDestroy(): void {
    const filename = LocalStoreService.getItem(LocalStoreService.localItemKeys.followUpFilename);
    if (filename) {
      LocalStoreService.deleteItem(filename);
      LocalStoreService.deleteItem(LocalStoreService.localItemKeys.followUpFilename);
    }
  }
}
